import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Progress,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaBuilding, FaClipboardList, FaExclamationTriangle, FaFileAlt, FaUsers } from 'react-icons/fa';
import { useUser } from '../../contexts/UserContext';
import {
  canAccessAdminSection,
  canManageConsultantsAdmin,
  canViewFeatureToggles,
  canViewLeadsAdmin,
} from '../../utils/authorization';
import { getAdminDashboard } from './adminDashboardApi';

const emptyStats = {
  organizations: null,
  organizationsInTrial: null,
  totalNetworks: null,
  organizationsInNetworks: null,
  users: null,
  responses: null,
};

const emptyDashboard = {
  stats: emptyStats,
  surveyCoverage: null,
  recentActivity: [],
  recentOrganizations: [],
  consultantActivity: [],
};

const toNumberOrNull = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const toDisplayText = (value, fallback = 'Unknown') => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || fallback;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value && typeof value === 'object') {
    const candidates = [
      value?.name,
      value?.label,
      value?.title,
      value?.message,
      value?.description,
      value?.type,
      value?.value,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
      if (typeof candidate === 'number' || typeof candidate === 'boolean') return String(candidate);
    }
  }

  return fallback;
};

const formatMetricValue = (value, { suffix = '', decimals = 0 } = {}) => {
  const number = toNumberOrNull(value);
  if (number == null) return '—';
  return `${number.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix}`;
};

const formatCoverageValue = (covered, total) => {
  const coveredNumber = toNumberOrNull(covered);
  const totalNumber = toNumberOrNull(total);

  if (coveredNumber == null || totalNumber == null) return '—';
  return `${coveredNumber} / ${totalNumber}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatRelativeTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffMinutes = Math.max(0, Math.round((Date.now() - date.getTime()) / (1000 * 60)));
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatDate(value);
};

const getActivityMessage = (item) =>
  toDisplayText(item?.message, '') ||
  toDisplayText(item?.description, '') ||
  toDisplayText(item?.title, '') ||
  toDisplayText(item?.label, 'Activity update');

const getActivityTimestamp = (item) =>
  item?.timestamp || item?.created_at || item?.occurred_at || item?.inserted_at || null;

const getActivityIcon = (type) => {
  const normalized = typeof type === 'string' ? type.toLowerCase() : '';
  if (normalized.includes('organization')) return FaBuilding;
  if (normalized.includes('survey')) return FaClipboardList;
  if (normalized.includes('user') || normalized.includes('admin')) return FaUsers;
  if (normalized.includes('response')) return FaFileAlt;
  return FaClipboardList;
};

const getRecentOrganizationName = (item) =>
  toDisplayText(item?.name, '') ||
  toDisplayText(item?.organization_name, 'Unknown school');
const getRecentOrganizationCreatedAt = (item) => item?.created_at || item?.inserted_at || null;
const getCreatedByName = (item) =>
  toDisplayText(item?.created_by?.name, '') ||
  toDisplayText(item?.created_by, '') ||
  toDisplayText(item?.creator?.name, '') ||
  toDisplayText(item?.creator_type, '') ||
  toDisplayText(item?.source, 'System');
const getConsultantName = (item) =>
  toDisplayText(item?.consultant?.name, '') ||
  toDisplayText(item?.name, '') ||
  toDisplayText(item?.consultant_name, 'Unknown consultant');
const getConsultantOrgCount = (item) => toNumberOrNull(item?.org_count ?? item?.organizations_count ?? item?.count);
const getConsultantRecentOrgs = (item) =>
  toNumberOrNull(item?.recent_orgs_created ?? item?.recent_organizations_created ?? item?.recent_count);

const AdminDashboard = () => {
  const { user } = useUser();
  const canAccess = canAccessAdminSection(user);
  const canFeatureToggles = canViewFeatureToggles(user);
  const canLeads = canViewLeadsAdmin(user);
  const canConsultants = canManageConsultantsAdmin(user);
  const hasHkeAdminRole = Array.isArray(user?.roles) && user.roles.includes('hke_admin');

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const activityIconBg = useColorModeValue('gray.100', 'whiteAlpha.200');

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [rawDashboard, setRawDashboard] = useState(null);

  console.log('✅ Admin Dashboard Loaded');
  console.log('Current user:', user);
  console.log('Current user has HKE admin role:', hasHkeAdminRole);

  useEffect(() => {
    if (!canAccess) return;

    const loadDashboard = async () => {
      setIsLoading(true);
      setLoadError(false);
      try {
        const response = await getAdminDashboard();
        console.log('Admin dashboard response:', response);
        setRawDashboard(response?.payload ?? null);
        setDashboard({
          stats: response?.stats || emptyStats,
          surveyCoverage: response?.surveyCoverage ?? null,
          recentActivity: Array.isArray(response?.recentActivity) ? response.recentActivity : [],
          recentOrganizations: Array.isArray(response?.recentOrganizations) ? response.recentOrganizations : [],
          consultantActivity: Array.isArray(response?.consultantActivity) ? response.consultantActivity : [],
        });
      } catch (error) {
        console.error('Error loading admin dashboard:', error);
        setLoadError(true);
        setDashboard(emptyDashboard);
        setRawDashboard(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboard();
  }, [canAccess]);

  const surveyCoverage = dashboard.surveyCoverage;
  console.log('Survey coverage:', surveyCoverage);
  const hasSurveyCoverage = surveyCoverage != null;
  const total = hasSurveyCoverage ? (surveyCoverage?.total_organizations ?? 0) : null;
  const withSurveys = hasSurveyCoverage ? (surveyCoverage?.organizations_with_surveys ?? 0) : null;
  const withResponses = hasSurveyCoverage ? (surveyCoverage?.organizations_with_responses ?? 0) : null;
  const surveyCoveragePercent =
    total > 0 ? Math.round((withSurveys / total) * 100) : 0;
  const responseCoveragePercent =
    total > 0 ? Math.round((withResponses / total) * 100) : 0;
  const needsAttention = rawDashboard?.needs_attention || {};
  console.log('needs_attention:', needsAttention);
  const {
    organizations_without_surveys = 0,
    organizations_without_responses = 0,
    organizations_without_admins = 0,
  } = needsAttention;
  const needsAttentionItems = [
    {
      key: 'without-surveys',
      label: 'Schools without surveys',
      value: organizations_without_surveys,
      tooltip: 'Schools that have not created any surveys yet. These are not using the platform.',
    },
    {
      key: 'without-responses',
      label: 'Schools without responses',
      value: organizations_without_responses,
      tooltip: 'Schools with surveys but no responses. Indicates low engagement or rollout issues.',
    },
    {
      key: 'without-admins',
      label: 'Schools without admins',
      value: organizations_without_admins,
      tooltip: 'Schools without an assigned admin. These are at high risk due to lack of ownership.',
    },
  ];
  const metricCards = useMemo(() => [
    {
      label: 'Total Schools',
      value: formatMetricValue(dashboard.stats.organizations),
      helpText: 'All schools',
      colorScheme: 'green',
    },
    {
      label: 'Schools in Trial',
      // TODO: Replace this fallback when the admin dashboard payload exposes a canonical trial count.
      value: formatMetricValue(dashboard.stats.organizationsInTrial),
      helpText: 'Current trial schools',
      colorScheme: 'green',
    },
    {
      label: 'Total Users',
      value: formatMetricValue(dashboard.stats.users),
      helpText: 'Users across the platform',
      colorScheme: 'green',
    },
    {
      label: 'Total Responses',
      value: formatMetricValue(dashboard.stats.responses),
      helpText: 'Tracked responses',
      colorScheme: 'green',
    },
    {
      label: 'Total Networks',
      value: formatMetricValue(dashboard.stats.totalNetworks ?? 0),
      helpText: 'Configured networks',
      colorScheme: 'blue',
    },
    {
      label: 'Schools in Networks',
      value: formatMetricValue(dashboard.stats.organizationsInNetworks ?? 0),
      helpText: 'Schools linked to networks',
      colorScheme: 'blue',
    },
    {
      label: 'Schools with Surveys',
      value: formatCoverageValue(withSurveys, total),
      helpText: hasSurveyCoverage ? `${surveyCoveragePercent}% adoption` : 'No coverage data',
      colorScheme: 'green',
    },
    {
      label: 'Schools with Responses',
      value: formatCoverageValue(withResponses, total),
      helpText: hasSurveyCoverage ? `${responseCoveragePercent}% adoption` : 'No coverage data',
      colorScheme: 'blue',
    },
  ], [dashboard.stats, hasSurveyCoverage, responseCoveragePercent, surveyCoveragePercent, total, withResponses, withSurveys]);

  if (!canAccess) {
    return <Box p={6}><Heading size="md">Access Denied</Heading></Box>;
  }

  const ActionButton = ({ to, colorScheme, children }) => (
    <Button as={RouterLink} to={to} variant="outline" colorScheme={colorScheme} size="sm">
      {children}
    </Button>
  );

  return (
    <Box p={{ base: 4, md: 6 }} mx="auto">
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading as="h2" size="lg">Admin</Heading>
          <Text mt={1} color={mutedTextColor}>
            Insight-driven view of coverage, risk, and consultant momentum.
          </Text>
        </Box>

        {isLoading ? (
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <HStack spacing={3}>
                <Spinner size="sm" />
                <Text>Loading dashboard...</Text>
              </HStack>
            </CardBody>
          </Card>
        ) : null}

        {loadError ? (
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Text color="red.500">Failed to load dashboard</Text>
            </CardBody>
          </Card>
        ) : null}

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6}>
          {metricCards.map((metric) => (
            <Card key={metric.label} bg={cardBg} borderWidth="1px" borderColor={borderColor}>
              <CardBody>
                <Stat>
                  <StatLabel color={mutedTextColor}>{metric.label}</StatLabel>
                  <StatNumber>{isLoading ? '—' : metric.value}</StatNumber>
                  <StatHelpText mb={0}>
                    <Badge colorScheme={metric.colorScheme} variant="subtle" borderRadius="full" px={2}>
                      {metric.helpText}
                    </Badge>
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardHeader pb={0}>
              <Heading size="md">Survey Coverage</Heading>
            </CardHeader>
            <CardBody>
              {!surveyCoverage ? (
                <Text>No data available</Text>
              ) : total === 0 ? (
                <Text>No schools yet</Text>
              ) : (
                <VStack spacing={5} align="stretch">
                  <Box>
                    <Text fontWeight="medium" mb={2}>Schools with surveys</Text>
                    <Text color={mutedTextColor} mb={2}>
                      {`${withSurveys} / ${total} (${surveyCoveragePercent}%)`}
                    </Text>
                    <Progress value={surveyCoveragePercent} size="sm" borderRadius="full" colorScheme="green" />
                  </Box>

                  <Box>
                    <Text fontWeight="medium" mb={2}>Schools with responses</Text>
                    <Text color={mutedTextColor} mb={2}>
                      {`${withResponses} / ${total} (${responseCoveragePercent}%)`}
                    </Text>
                    <Progress value={responseCoveragePercent} size="sm" borderRadius="full" colorScheme="blue" />
                  </Box>
                </VStack>
              )}
            </CardBody>
          </Card>

          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardHeader pb={0}>
              <Heading size="md">Recent Schools</Heading>
            </CardHeader>
            <CardBody>
              {isLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner />
                </Flex>
              ) : dashboard.recentOrganizations.length === 0 ? (
                <Flex minH="180px" align="center" justify="center">
                  <Text color={mutedTextColor}>No data available</Text>
                </Flex>
              ) : (
                <VStack spacing={4} align="stretch">
                  {dashboard.recentOrganizations.map((item, index) => (
                    <HStack
                      key={`${getRecentOrganizationName(item)}-${index}`}
                      spacing={4}
                      align="start"
                      p={3}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                    >
                      <Flex
                        w="10"
                        h="10"
                        borderRadius="full"
                        align="center"
                        justify="center"
                        bg={activityIconBg}
                        flexShrink={0}
                      >
                        <Icon as={FaBuilding} />
                      </Flex>
                      <Box minW={0}>
                        <HStack spacing={2} flexWrap="wrap" mb={1}>
                          <Text fontWeight="medium">{getRecentOrganizationName(item)}</Text>
                          {(item?.surveys_count == null || Number(item?.surveys_count) === 0) ? (
                            <Badge colorScheme="yellow" borderRadius="full">No surveys</Badge>
                          ) : null}
                          {(item?.responses_count == null || Number(item?.responses_count) === 0) ? (
                            <Badge colorScheme="orange" borderRadius="full">No responses</Badge>
                          ) : null}
                        </HStack>
                        <Text fontSize="sm" color={mutedTextColor}>
                          {formatRelativeTime(getRecentOrganizationCreatedAt(item))}
                        </Text>
                        <Text fontSize="sm" color={mutedTextColor}>
                          {`Created by ${getCreatedByName(item)}`}
                        </Text>
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              )}
            </CardBody>
          </Card>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardHeader pb={0}>
              <Heading size="md">Needs Attention</Heading>
            </CardHeader>
            <CardBody>
              {isLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner />
                </Flex>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  {needsAttentionItems.map((item) => (
                    <Card key={item.key} variant="outline" borderColor={borderColor}>
                      <CardBody>
                        <VStack align="start" spacing={2}>
                          <Tooltip label={item.tooltip} hasArrow placement="top-start">
                            <Text fontWeight="semibold" textDecoration="underline dotted" cursor="help">
                              {item.label}
                            </Text>
                          </Tooltip>
                          <Text fontSize="2xl" fontWeight="bold" color={item.value > 0 ? 'red.400' : mutedTextColor}>
                            {formatMetricValue(item.value)}
                          </Text>
                          <Text fontSize="sm" color={mutedTextColor}>{item.tooltip}</Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </CardBody>
          </Card>

          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardHeader pb={0}>
              <Heading size="md">Consultant Activity</Heading>
            </CardHeader>
            <CardBody>
              {isLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner />
                </Flex>
              ) : dashboard.consultantActivity.length === 0 ? (
                <Flex minH="180px" align="center" justify="center">
                  <Text color={mutedTextColor}>No data available</Text>
                </Flex>
              ) : (
                <TableContainer>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Consultant</Th>
                        <Th isNumeric>Org Count</Th>
                        <Th isNumeric>Recent Orgs Created</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {dashboard.consultantActivity.map((item, index) => (
                        <Tr key={`${getConsultantName(item)}-${index}`}>
                          <Td>{getConsultantName(item)}</Td>
                          <Td isNumeric>{formatMetricValue(getConsultantOrgCount(item))}</Td>
                          <Td isNumeric>{formatMetricValue(getConsultantRecentOrgs(item))}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </CardBody>
          </Card>
        </SimpleGrid>

        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardHeader pb={0}>
            <Heading size="md">Recent Activity</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={8}>
                <Spinner />
              </Flex>
            ) : dashboard.recentActivity.length === 0 ? (
              <Flex minH="180px" align="center" justify="center">
                <Text color={mutedTextColor}>No data available</Text>
              </Flex>
            ) : (
              <VStack spacing={4} align="stretch">
                {dashboard.recentActivity.map((item, index) => (
                  <HStack
                    key={`${getActivityMessage(item)}-${index}`}
                    spacing={4}
                    align="start"
                    p={3}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="lg"
                  >
                    <Flex
                      w="10"
                      h="10"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      bg={activityIconBg}
                      flexShrink={0}
                    >
                      <Icon as={getActivityIcon(item?.type)} />
                    </Flex>
                    <Box minW={0}>
                      <Text fontWeight="medium">{getActivityMessage(item)}</Text>
                      <Text fontSize="sm" color={mutedTextColor}>
                        {formatDateTime(getActivityTimestamp(item))}
                      </Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardHeader pb={0}>
            <Heading size="md">Admin Actions</Heading>
          </CardHeader>
          <CardBody>
            <HStack spacing={3} flexWrap="wrap">
              {canFeatureToggles ? (
                <ActionButton to="/app/admin/feature-toggles" colorScheme="blue">
                  Manage Feature Toggles
                </ActionButton>
              ) : null}
              {canLeads ? (
                <ActionButton to="/app/admin/tour-leads" colorScheme="green">
                  View Tour Leads
                </ActionButton>
              ) : null}
              {canConsultants ? (
                <ActionButton to="/app/admin/consultants" colorScheme="purple">
                  Manage Consultants
                </ActionButton>
              ) : null}
              {canLeads ? (
                <ActionButton to="/app/admin/leads" colorScheme="teal">
                  View Leads
                </ActionButton>
              ) : null}
            </HStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default AdminDashboard;
